import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { translations, phoneRaw } from '../data/menuData';
import { CartItem } from './CartItem';
import { CartFooter } from './CartFooter';

const EMPTY_INITIAL = { opacity: 0, scale: 0.9 };
const EMPTY_ANIMATE = { opacity: 1, scale: 1 };
const EMPTY_TRANSITION = { duration: 0.3 };

const EmptyCartMessage = ({ emptyText, addProductsText }) => (
  <motion.div
    initial={EMPTY_INITIAL}
    animate={EMPTY_ANIMATE}
    transition={EMPTY_TRANSITION}
    className="rounded-2xl border-2 border-dashed border-border p-8 text-center"
    data-testid="empty-cart-message"
  >
    <p className="text-muted-foreground">{emptyText}</p>
    <p className="mt-2 text-sm text-muted-foreground">{addProductsText}</p>
  </motion.div>
);

export const CartSheet = () => {
  const { cart, removeFromCart, isCartOpen, setIsCartOpen, language } = useApp();
  const t = translations[language];

  const generateWhatsAppMessage = () => {
    if (cart.length === 0) return encodeURIComponent(t.whatsappGreeting);
    const orderList = cart
      .map((item, i) => `${i + 1}. ${item.name} - ${item.price}`)
      .join('%0A');
    return encodeURIComponent(`${t.whatsappGreeting}%0A%0A${orderList}`);
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-lg" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="text-2xl font-bold">{t.yourOrder}</SheetTitle>
          <SheetDescription>{t.orderSummary}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex flex-col h-[calc(100vh-12rem)]">
          <ScrollArea className="flex-1 pr-4">
            {cart.length === 0 ? (
              <EmptyCartMessage emptyText={t.emptyCart} addProductsText={t.addProducts} />
            ) : (
              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {cart.map((item, index) => (
                    <CartItem key={item.id} item={item} index={index} onRemove={removeFromCart} removeText={t.remove} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </ScrollArea>
          <CartFooter
            whatsappUrl={`https://wa.me/${phoneRaw}?text=${generateWhatsAppMessage()}`}
            sendOrderText={t.sendOrder}
            callText={t.callRestaurant}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
